import { captureException } from "@sentry/browser";
import type { Doc } from "building-types/doc";
import type { BuildingGenerationError } from "building-types/errors/buildingGenerationError";
import {
  BuildingGenerationErrorBase,
  type BuildingGenerationErrorType,
} from "building-types/errors/buildingGenerationError";
import type { LevelHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import { docGetEntityName } from "doc/docGetEntityName";
import { polyLine2EdgesOverlap } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ExportPlanSuccessEvent } from "lib/components/builders/communities/utils/exportPlanTypes";
import { formatBuildingErrorMessage } from "lib/components/studio/ErrorHandling";
import { addRumError } from "lib/datadogRum";

export interface FailingRoom {
  readonly handle: RoomHandle;
  readonly name: string;
  /** The level the doc files this room under, which need not be the level the error names. */
  readonly levelId: string | undefined;
  readonly levelName: string | undefined;
}

export interface BuildingGenerationFailureDetails {
  readonly errorType: BuildingGenerationErrorType;
  readonly handleId: string | undefined;
  readonly handleName: string | undefined;
  readonly rooms: FailingRoom[];
  readonly geometry: PolyLine2[];
  readonly issueCount: number;
  readonly message: string;
}

/** Plain-JSON form: `PolyLine2` is a `Float64Array`, which does not stringify usefully. */
export interface SerializedFailureDetails extends Omit<BuildingGenerationFailureDetails, "errorType" | "geometry"> {
  readonly errorType: string;
  readonly geometry: number[][];
}

const failureDetailsKey: unique symbol = Symbol("buildingGenerationFailureDetails");

interface ErrorWithFailureDetails {
  [failureDetailsKey]?: BuildingGenerationFailureDetails;
}

/**
 * Finds the rooms whose outlines share a real edge with the failing geometry.
 *
 * Searches the whole document rather than the level the error names — generation places
 * component-instance rooms onto a level the doc files them away from, so filtering by the
 * reported level misses exactly the rooms that caused the failure.
 */
export function attributeFailingRooms(doc: Doc, geometry: PolyLine2[]): FailingRoom[] {
  if (geometry.length === 0) {
    return [];
  }

  const rooms: FailingRoom[] = [];
  for (const room of Object.values(doc.rooms)) {
    if (!geometry.some((sliver) => polyLine2EdgesOverlap(sliver, room.outline))) {
      continue;
    }

    const handle: RoomHandle = { type: "ROOM", id: room.id };
    const levelId = doc.blocks[room.parentId]?.levelId;
    const levelHandle: LevelHandle | undefined = levelId ? { type: "LEVEL", id: levelId } : undefined;
    rooms.push({
      handle,
      name: docGetEntityName(doc, handle),
      levelId,
      levelName: levelHandle ? docGetEntityName(doc, levelHandle) : undefined,
    });
  }

  return rooms;
}

export function serializeFailureDetails(details: BuildingGenerationFailureDetails): SerializedFailureDetails {
  return {
    ...details,
    errorType: details.errorType,
    geometry: details.geometry.map((polyline) => Array.from(polyline)),
  };
}

/** Attaches diagnostic details without changing the error's enumerable shape. */
export function attachFailureDetails(error: unknown, details: BuildingGenerationFailureDetails): void {
  if ((typeof error !== "object" && typeof error !== "function") || error === null) {
    return;
  }

  Object.defineProperty(error, failureDetailsKey, { value: details, configurable: true });
}

export function readFailureDetails(error: unknown): BuildingGenerationFailureDetails | undefined {
  if ((typeof error !== "object" && typeof error !== "function") || error === null) {
    return undefined;
  }

  return (error as ErrorWithFailureDetails)[failureDetailsKey];
}

/**
 * Records a building generation failure to the console, Datadog RUM, and Sentry.
 *
 * @param flow - the export that failed; tagged on Sentry and faceted on RUM so the
 *   two main-thread download paths can be told apart.
 * @returns the recorded payload, or undefined when the error was not a generation failure.
 */
export function reportBuildingGenerationFailure(
  error: unknown,
  doc: Doc,
  flow: ExportPlanSuccessEvent,
): BuildingGenerationFailureDetails | undefined {
  if (!(error instanceof BuildingGenerationErrorBase)) {
    return undefined;
  }

  const generationError = error as BuildingGenerationError;
  const message = formatBuildingErrorMessage(doc, generationError);
  const handleId = generationError.handle && "id" in generationError.handle ? generationError.handle.id : undefined;
  const handleName = generationError.handle ? docGetEntityName(doc, generationError.handle) : undefined;
  const rooms = attributeFailingRooms(doc, generationError.geometry);
  const details: BuildingGenerationFailureDetails = {
    errorType: generationError.type,
    handleId,
    handleName,
    rooms,
    geometry: generationError.geometry,
    issueCount: generationError.partialBuilding?.issues?.length ?? 0,
    message,
  };
  const payload = serializeFailureDetails(details);

  try {
    console.error(`${flow} failed during building generation: ${message}`, details, error);
  } catch {}

  try {
    addRumError(error, { flow, ...payload });
  } catch {}

  try {
    captureException(error, { tags: { exportFlow: flow }, extra: { ...payload } });
  } catch {}

  return details;
}
